import type { Message, ToolUseBlock } from './types';

const DEFAULT_STATUS = 'Working';

const TOOL_LABELS: Record<string, string> = {
  keyword_search: 'Searching knowledge base',
  get_document: 'Reading document',
  create_document: 'Creating document',
  update_document: 'Updating document',
};

function labelForToolUse(block: ToolUseBlock): string {
  // Normalise both snake_case and camelCase tool names to a single key.
  const key = block.name
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();

  // Fall back to the generic status for unrecognised tools rather than
  // surfacing raw wire tool names in the UI.
  return TOOL_LABELS[key] ?? DEFAULT_STATUS;
}

/**
 * Derive the live status label from a message's latest content block, or `null`
 * when no status should be shown.
 *
 * The status reflects only the most recent activity ("latest only"): a tool
 * call shows its mapped label, thinking shows "Thinking", and a tool result
 * keeps the originating tool's label so it doesn't flicker to generic between
 * the result arriving and the next block.
 *
 * It is shown before the first answer text and while tools/thinking are in
 * flight, but hidden once answer text is actively streaming — returning `null`
 * when the latest block is a text block that already has content.
 */
export function getConversationStatusLabel(message: Message): string | null {
  const blocks = message.content;
  if (blocks.length === 0) {
    return DEFAULT_STATUS;
  }

  const latest = blocks[blocks.length - 1];
  switch (latest.type) {
    case 'tool_use':
      return labelForToolUse(latest);
    case 'tool_result': {
      const origin = blocks.findLast((b): b is ToolUseBlock => b.type === 'tool_use' && b.id === latest.toolUseId);

      return origin ? labelForToolUse(origin) : DEFAULT_STATUS;
    }
    case 'thinking':
      return 'Thinking';
    case 'text':
      // Hide the loader while the answer streams. If the text block has only
      // just opened (no content yet), keep it up so there's no flicker between
      // the last tool call and the first character of the answer.
      return latest.text.length > 0 ? null : DEFAULT_STATUS;
    default:
      return DEFAULT_STATUS;
  }
}
